/**
 * CHROME STATE (B10) — pure rules for when the UI shows, arrives, dims and sleeps.
 * Tested in arrival.test.ts. The DOM side lives in chrome.ts.
 *
 *   ui       hidden   loading, and the intro until its "ui" beat (§E.7, 8300 ms)
 *            arriving from the beat (or the first explore) for ARRIVAL_MS
 *            shown    after that
 *   chrome   live     normal
 *            dim      drive: opacity .35 and inert, except [data-ld-drive="live"] (§E.6)
 *            asleep   kiosk attract: inert, not dimmed (§G "the panel becomes inert")
 */
import type { Phase } from "@/contracts/store";

export type UiState = "hidden" | "arriving" | "shown";
export type ChromeState = "live" | "dim" | "asleep";

/** Phases in which the chrome is on screen. */
export const SHOWN_PHASES: ReadonlySet<Phase> = new Set<Phase>(["explore", "drive", "attract"]);

/** §E.7 ui beat: 640 settle, translate 12 → 0, stagger 70 over five items (panel, light, zoom, tools, attribution). */
export const ARRIVAL = { ms: 640, stagger: 70, rise: 12, items: 5 } as const;
/** How long "arriving" lasts: the last item's delay + its duration + a frame. */
export const ARRIVAL_MS = ARRIVAL.ms + (ARRIVAL.items - 1) * ARRIVAL.stagger + 40;
/** Reduced motion: a 160 ms fade (§E.10). */
export const ARRIVAL_REDUCED_MS = 160;

/** §E.6: chrome to .35 over 420 drift; back over 420 when the drive ends. */
export const DRIVE_DIM = { opacity: 0.35, ms: 420 } as const;

/** Chrome is visible in explore / drive / attract, and in the intro once the "ui" beat has played. */
export function uiVisible(phase: Phase, uiBeatSeen: boolean): boolean {
  return SHOWN_PHASES.has(phase) || (phase === "intro" && uiBeatSeen);
}

/** hidden → arriving on becoming visible; anything → hidden when not visible. arriving → shown is timed by the caller. */
export function nextUiState(prev: UiState, visible: boolean): UiState {
  if (!visible) return "hidden";
  return prev === "hidden" ? "arriving" : prev;
}

/** Stagger only for the full intro (arrival at the beat) and never under reduced motion (§E.7 short intro). */
export function arrivalStagger(fromBeat: boolean, reduced: boolean): boolean {
  return fromBeat && !reduced;
}

export function chromeState(phase: Phase): ChromeState {
  if (phase === "drive") return "dim";
  if (phase === "attract") return "asleep";
  return "live";
}
